import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './i18n/LanguageContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Portal from './pages/Portal';
import TabletApp from './pages/TabletApp';
// #222 PR-3 — `Intake` (legacy voice-mode intake) was deleted.
// Form-first intake at /intake/review is the only path now;
// /intake redirects there for any stale bookmarks / QR codes.
import AdminLogin from './admin/AdminLogin';
import AdminGuard from './admin/AdminGuard';
import AdminDashboard from './admin/AdminDashboard';
import AdminUsers from './admin/AdminUsers';
import AdminUserDetail from './admin/AdminUserDetail';
import AdminUserHealthConcerns from './admin/AdminUserHealthConcerns';
import AdminUserConversations from './admin/AdminUserConversations';
import AdminUserMemory from './admin/AdminUserMemory';
// #1028 — per-user Reminders & callbacks sub-page.
import AdminUserReminders from './admin/AdminUserReminders';
import AdminUserNewsTopics from './admin/AdminUserNewsTopics';
import AdminUserSettings from './admin/AdminUserSettings';
import AdminUserOpenThreads from './admin/AdminUserOpenThreads';
import AdminUserPersona from './admin/AdminUserPersona';
// #873 — per-user Cards sub-page (topic-activity charts + grid).
import AdminUserCards from './admin/AdminUserCards';
import AdminUsage from './admin/AdminUsage';
import AdminUnitEconomics from './admin/AdminUnitEconomics';
import AdminConversations from './admin/AdminConversations';
import AdminConversationDetail from './admin/AdminConversationDetail';
import AdminSystem from './admin/AdminSystem';
import AdminIntakeQuestions from './admin/AdminIntakeQuestions';
import AdminSecurity from './admin/AdminSecurity';
import AdminAnalytics from './admin/AdminAnalytics';
import AdminVoices from './admin/AdminVoices';
import AdminLlmConfig from './admin/AdminLlmConfig';
// #765 Story D — admin observability views.
import AdminUserEngagement from './admin/AdminUserEngagement';
import AdminCardsEffectiveness from './admin/AdminCardsEffectiveness';
// #766 Story E — admin observability views.
import AdminCardsVariety from './admin/AdminCardsVariety';
import AdminConversationsDisengaged from './admin/AdminConversationsDisengaged';
import AdminCardsMoodMatrix from './admin/AdminCardsMoodMatrix';
import AdminCardsWildcardGaps from './admin/AdminCardsWildcardGaps';
// #959 — admin Crons observability.
import AdminCrons from './admin/AdminCrons';
import AdminCronNewsCrawler from './admin/AdminCronNewsCrawler';
import IntakeReview from './pages/IntakeReview';

// Get user_id from URL param or localStorage.
// If URL has ?user=UUID, persist it so the tablet remembers across reloads.
// If URL has ?setup=TOKEN, activate via backend first.
function getTabletUserId() {
  const params = new URLSearchParams(window.location.search);

  // Legacy: direct ?user=UUID param
  const fromUrl = params.get('user');
  if (fromUrl) {
    localStorage.setItem('tablet_user_id', fromUrl);
    return fromUrl;
  }

  // New: ?setup=TOKEN triggers activation (handled async in TabletApp)
  const setupToken = params.get('setup');
  if (setupToken) return null; // TabletApp handles activation

  return localStorage.getItem('tablet_user_id') || null;
}

export default function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/"        element={<Landing />} />
          <Route path="/login"   element={<Login />} />
          <Route path="/signup"  element={<Signup />} />
          <Route path="/portal"  element={<Portal />} />
          <Route path="/portal/*" element={<Portal />} />
          <Route path="/app"     element={<TabletApp userId={getTabletUserId()} />} />
          {/* #222 PR-3 — voice intake removed; redirect to form-first */}
          <Route path="/intake"  element={<Navigate to="/intake/review" replace />} />
          <Route path="/intake/review" element={<IntakeReview />} />
          {/* Legacy: bare ?user= still works */}
          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
          <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
          <Route path="/admin/users/:id" element={<AdminGuard><AdminUserDetail /></AdminGuard>} />
          {/* #753 — per-user sub-pages, one per tile in the dashboard. */}
          <Route path="/admin/users/:id/health-concerns" element={<AdminGuard><AdminUserHealthConcerns /></AdminGuard>} />
          <Route path="/admin/users/:id/conversations"   element={<AdminGuard><AdminUserConversations /></AdminGuard>} />
          <Route path="/admin/users/:id/memory"          element={<AdminGuard><AdminUserMemory /></AdminGuard>} />
          {/* #1028 — per-user Reminders & callbacks sub-page. */}
          <Route path="/admin/users/:id/reminders"       element={<AdminGuard><AdminUserReminders /></AdminGuard>} />
          <Route path="/admin/users/:id/news-topics"     element={<AdminGuard><AdminUserNewsTopics /></AdminGuard>} />
          <Route path="/admin/users/:id/settings"        element={<AdminGuard><AdminUserSettings /></AdminGuard>} />
          {/* #765 Story D View 2 — per-user engagement trajectory. */}
          <Route path="/admin/users/:id/engagement"      element={<AdminGuard><AdminUserEngagement /></AdminGuard>} />
          {/* #767 Story F — per-user open-threads admin view. */}
          <Route path="/admin/users/:id/open-threads"    element={<AdminGuard><AdminUserOpenThreads /></AdminGuard>} />
          {/* #845 — per-user persona sub-page (read-only). */}
          <Route path="/admin/users/:id/persona"         element={<AdminGuard><AdminUserPersona /></AdminGuard>} />
          {/* #873 — per-user Cards sub-page (topic-activity dashboard + grid). */}
          <Route path="/admin/users/:id/cards"           element={<AdminGuard><AdminUserCards /></AdminGuard>} />
          <Route path="/admin/usage" element={<AdminGuard><AdminUsage /></AdminGuard>} />
          <Route path="/admin/unit-economics" element={<AdminGuard><AdminUnitEconomics /></AdminGuard>} />
          <Route path="/admin/conversations" element={<AdminGuard><AdminConversations /></AdminGuard>} />
          <Route path="/admin/conversations/:id" element={<AdminGuard><AdminConversationDetail /></AdminGuard>} />
          <Route path="/admin/system" element={<AdminGuard><AdminSystem /></AdminGuard>} />
          <Route path="/admin/intake-questions" element={<AdminGuard><AdminIntakeQuestions /></AdminGuard>} />
          <Route path="/admin/security" element={<AdminGuard><AdminSecurity /></AdminGuard>} />
          <Route path="/admin/analytics" element={<AdminGuard><AdminAnalytics /></AdminGuard>} />
          <Route path="/admin/voices" element={<AdminGuard><AdminVoices /></AdminGuard>} />
          <Route path="/admin/llm-config" element={<AdminGuard><AdminLlmConfig /></AdminGuard>} />
          {/* #765 Story D View 3 — population card-type effectiveness. */}
          <Route path="/admin/cards/effectiveness" element={<AdminGuard><AdminCardsEffectiveness /></AdminGuard>} />
          {/* #766 Story E Views 4-6 — variety regression, disengaged calls, mood × card matrix. */}
          <Route path="/admin/cards/variety" element={<AdminGuard><AdminCardsVariety /></AdminGuard>} />
          <Route path="/admin/cards/mood-matrix" element={<AdminGuard><AdminCardsMoodMatrix /></AdminGuard>} />
          {/* #806 — wildcard taxonomy-gap detector. */}
          <Route path="/admin/cards/wildcard-gaps" element={<AdminGuard><AdminCardsWildcardGaps /></AdminGuard>} />
          <Route path="/admin/conversations/disengaged" element={<AdminGuard><AdminConversationsDisengaged /></AdminGuard>} />
          {/* #959 — admin Crons observability + News Crawler detail. */}
          <Route path="/admin/crons" element={<AdminGuard><AdminCrons /></AdminGuard>} />
          <Route path="/admin/crons/news-crawler" element={<AdminGuard><AdminCronNewsCrawler /></AdminGuard>} />
          <Route path="*"        element={<Landing />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}
